import { Component, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { LAYOUT_TYPE } from '../../model/common.model';
import { GoogleAnalyticsDealerOptionsSearchConfig, GoogleAnalyticsFilterSchema, GoogleAnalyticsJobCodeOptionsSearchConfig, GoogleAnalyticsReportOwnersSearchConfig, GoogleAnalyticsSearchConfig, GoogleAnalyticsTableSchema, GoogleAnalyticsTitle } from '../../model/google-analytics.model';
import { DynamicFormArray, FieldOption, FieldSelectedValue, Props } from '../../model/form.model';
import { BaseComponent } from '../base/base.component';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { ApiEnvNames, ERROR_RESPONSE, SearchConfig } from '../../model/api.model';
import { CommonService } from '../../services/common.service';
import { ApiService } from '../../services/api/api.service';
import { FieldNames } from '../../model/form-fields.model';

@Component({
  selector: 'app-google-analytics',
  imports: [PageContainerComponent],
  templateUrl: './google-analytics.component.html',
  styleUrl: './google-analytics.component.scss'
})

export class GoogleAnalyticsComponent extends BaseComponent {
  private readonly defaultReportName = 'Elevate Report';
  protected data = 'StaffMasterComponent';
  public override dataTableSchema: DataTableSchema = structuredClone(GoogleAnalyticsTableSchema);
  protected filterSchema: DynamicFormArray = structuredClone(GoogleAnalyticsFilterSchema);
  public props: Props = {};
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  title = GoogleAnalyticsTitle;
  service = inject(CommonService);
  private readonly apiService = inject(ApiService);
  private readonly http = inject(HttpClient);
  public searchConfig = GoogleAnalyticsSearchConfig;
  private rawData: any[] = [];
  private reportOwnerNameMap: Array<{ reportOwner: string, reportName: string }> = [];
  private reportOwnersRequest?: Promise<void>;
  private dealerOptionsRequest?: Promise<void>;
  private jobCodeOptionsRequest?: Promise<void>;
  private reportOwnersLoaded = false;
  private lastFetchedReportOwner = '';
  private lastFetchedReportName = '';
  private lastFetchedStartDate = '';
  private lastFetchedEndDate = '';
  private hasFetchedReportData = false;
  private readonly changedFilterFields = new Set<string>();

  override ngOnInit(): void {
    this.setTableLoadingState(true);
    super.ngOnInit();
    void this.fetchReportOwners();
    void this.fetchDealerOptions();
    void this.fetchJobCodeOptions();
  }

  override updateOptions = (): void => {};

  private toIsoDateString(value: any): string | null {
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) { return null; }
      const yyyy = value.getFullYear();
      const mm = String(value.getMonth() + 1).padStart(2, '0');
      const dd = String(value.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) { return str; }
    const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(str);
    if (mdy) {
      const [, mm, dd, yyyy] = mdy;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
      return this.toIsoDateString(parsed);
    }
    return null;
  }

  private toArray(value: any): string[] {
    if (Array.isArray(value)) { return value.filter(Boolean); }
    return value ? [value] : [];
  }

  private toComparable(value: any): string {
    return String(value ?? '').trim();
  }

  private toFilterComparable(value: any): string {
    return this.toComparable(value).toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  }

  private hasMatchingValue(selectedValue: any, rowValues: any[]): boolean {
    const selected = this.toFilterComparable(selectedValue);
    return !!selected && rowValues.some((value) => this.toFilterComparable(value) === selected);
  }

  private hasMatchingListValue(selectedValues: any[], rowValue: any): boolean {
    const rowComparable = this.toFilterComparable(rowValue);
    return selectedValues.some((value) => this.toFilterComparable(value) === rowComparable);
  }

  private formatDealerDisplay(row: any): string {
    const dealerCode = row?.['Dlr Code'] ?? row?.['DlrCode'] ?? row?.['dealerCode'] ?? '';
    const dealerName = row?.['Dealer'] ?? '';
    return [dealerCode, dealerName].filter(Boolean).join(' - ');
  }

  private mapTableRow(row: any, reportOwner: string, reportName = ''): any {
    return {
      ...row,
      reportOwner: row?.['reportOwner'] ?? reportOwner,
      reportName: row?.['reportName'] ?? row?.['Report Name'] ?? reportName,
      Dealer: this.formatDealerDisplay(row),
    };
  }

  private updateReportFieldOptions(): void {
    const ownerField = this.filterSchema.FieldsArray.find((p) => p.fieldName === FieldNames.ReportOwner);
    const reportNameField = this.filterSchema.FieldsArray.find((p) => p.fieldName === FieldNames.ReportName);
    const ownerOptions: FieldOption[] = this.reportOwnerNameMap.map(({ reportOwner }) => ({ fieldLabel: reportOwner, fieldValue: reportOwner }));
    const reportNameOptions: FieldOption[] = this.reportOwnerNameMap.map(({ reportName }) => ({ fieldLabel: reportName, fieldValue: reportName }));
    const defaultMapping = this.getDefaultReportMapping();
    if (ownerField) {
      ownerField.options = ownerOptions;
      if (defaultMapping) {
        ownerField.defaultValue = defaultMapping.reportOwner;
      }
      if (!ownerField.selectedValue && defaultMapping) {
        ownerField.selectedValue = defaultMapping.reportOwner;
      }
    }
    if (reportNameField) {
      reportNameField.options = reportNameOptions;
      if (defaultMapping) {
        reportNameField.defaultValue = defaultMapping.reportName;
      }
      if (!reportNameField.selectedValue && defaultMapping) {
        reportNameField.selectedValue = defaultMapping.reportName;
      } else if (!reportNameField.selectedValue && reportNameOptions.length) {
        const selectedOwner = ownerField?.selectedValue;
        reportNameField.selectedValue = this.reportOwnerNameMap.find((m) => m.reportOwner === selectedOwner)?.reportName ?? reportNameOptions[0].fieldValue;
      }
    }
  }

  private normalizeReportName(reportName: string): string {
    return reportName.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private getDefaultReportMapping(): { reportOwner: string, reportName: string } | undefined {
    return this.reportOwnerNameMap.find((item) => item.reportName === this.defaultReportName)
      ?? this.reportOwnerNameMap.find((item) => this.normalizeReportName(item.reportName) === this.normalizeReportName(this.defaultReportName))
      ?? this.reportOwnerNameMap.find((item) => this.normalizeReportName(item.reportName).includes('elevate'))
      ?? this.reportOwnerNameMap[0];
  }

  private isElevateReport(reportName: string): boolean {
    return this.normalizeReportName(reportName).includes('elevate');
  }

  private fetchReportOwners(): Promise<void> {
    if (this.reportOwnersRequest) {
      return this.reportOwnersRequest;
    }
    const baseUrl = (globalThis as any).environment?.[ApiEnvNames['GoogleAnalytics']];
    if (!baseUrl) {
      console.info(`Environment key '${ApiEnvNames['GoogleAnalytics']}' missing.`); 
      this.reportOwnersLoaded = true;
      return Promise.resolve();
    }
    const url = `${baseUrl}${GoogleAnalyticsReportOwnersSearchConfig.path}/{proxy+}`;
    const options = this.apiService.getHeader(GoogleAnalyticsReportOwnersSearchConfig.headers);
    this.reportOwnersRequest = lastValueFrom(this.http.get(url, options)).then((response: any) => {
      const payload = Array.isArray(response?.payload) ? response.payload : [];
      this.reportOwnerNameMap = payload
        .map((item: any) => ({ reportOwner: String(item?.ownerName ?? ''), reportName: String(item?.reportName ?? '') }))
        .filter((item: { reportOwner: string, reportName: string }) => item.reportOwner && item.reportName);
      this.updateReportFieldOptions();
    }).catch((err) => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);  
    }).finally(() => {
      this.reportOwnersLoaded = true;
    });
    return this.reportOwnersRequest;
  }

  private setFieldOptions(fieldName: string, options: FieldOption[]): void {
    const field = this.filterSchema.FieldsArray.find((item) => item.fieldName === fieldName);
    if (!field) {
      return;
    }
    field.options = options;
    field.isLoading = false;
  }

  private fetchDealerOptions(): Promise<void> {
    if (this.dealerOptionsRequest) {
      return this.dealerOptionsRequest;
    }
    this.dealerOptionsRequest = this.service.getDataFromDataSource(GoogleAnalyticsDealerOptionsSearchConfig, ApiEnvNames['JobEnrollment']).then((response: any) => {
      const dealers = response?.result?.[0]?.[FieldNames.DealerNumber] ?? [];
      const options: FieldOption[] = Array.isArray(dealers)
        ? dealers.map((dealer: any) => {
          const dealerName = String(dealer?.DEALER_NAME ?? '').trim();
          const dealerDisplay = dealerName;
          return { fieldLabel: dealerDisplay || dealerName, fieldValue: dealerName };
        }).filter((option: FieldOption) => option.fieldValue)
        : [];
      this.setFieldOptions(FieldNames.Dealer, options);
    }).catch((err) => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);  
      this.setFieldOptions(FieldNames.Dealer, []);
    });
    return this.dealerOptionsRequest;
  }

  private fetchJobCodeOptions(): Promise<void> {
    if (this.jobCodeOptionsRequest) {
      return this.jobCodeOptionsRequest;
    }
    this.jobCodeOptionsRequest = this.service.getDataFromDataSource(GoogleAnalyticsJobCodeOptionsSearchConfig, ApiEnvNames['EliteCertification']).then((response: any) => {
      const rows = Array.isArray(response?.result?.[0]?.payload) ? response.result[0].payload : response?.result;
      const options: FieldOption[] = Array.isArray(rows)
        ? rows
          .filter((item: any) => item?.lexusPrimaryJobCode)
          .map((item: any) => ({ fieldLabel: item.lexusPrimaryJobCode, fieldValue: item.lexusPrimaryJobCode }))
        : [];
      this.setFieldOptions(FieldNames.LexusPrimaryJobCode, options);
    }).catch((err) => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);  
      this.setFieldOptions(FieldNames.LexusPrimaryJobCode, []);
    });
    return this.jobCodeOptionsRequest;
  }

  private applyClientFilters(): void {
    const fields = this.filterSchema.FieldsArray;
    const getValue = (name: string) => fields.find((f) => f.fieldName === name)?.selectedValue;

    const areaList = this.toArray(getValue(FieldNames.Area));
    const districtList = this.toArray(getValue(FieldNames.District));
    const dealerValue = getValue(FieldNames.Dealer);
    const jobCodeValue = getValue(FieldNames.LexusPrimaryJobCode);
    const eventNameValue = getValue(FieldNames.EventName);
    const fromDate = this.changedFilterFields.has(FieldNames.StartDate) ? this.toIsoDateString(getValue(FieldNames.StartDate)) : null;
    const toDate = this.changedFilterFields.has(FieldNames.endDate) ? this.toIsoDateString(getValue(FieldNames.endDate)) : null;
    const reportOwnerValue = getValue(FieldNames.ReportOwner) || '';
    const reportNameValue = getValue(FieldNames.ReportName) || '';

    const filtered = this.rawData.filter((row) => {
      if (areaList.length && !this.hasMatchingListValue(areaList, row['Area'])) { return false; }
      if (districtList.length && !this.hasMatchingListValue(districtList, row['District'])) { return false; }
      if (dealerValue && !this.hasMatchingValue(dealerValue, [row['Dealer'], row['Dlr Code'], row['DlrCode'], row['dealerCode'], this.formatDealerDisplay(row)])) { return false; }
      if (jobCodeValue && !this.hasMatchingValue(jobCodeValue, [row['Job'], row['Job Code'], row['jobCode'], row['lexusPrimaryJobCode']])) { return false; }
      if (eventNameValue && !this.hasMatchingValue(eventNameValue, [row['Event Name'], row['eventName']])) { return false; }
      if ((fromDate || toDate) && row['Event Date']) {
        const rowDate = this.toIsoDateString(row['Event Date']);
        if (rowDate) {
          if (fromDate && rowDate < fromDate) { return false; }
          if (toDate && rowDate > toDate) { return false; }
        }
      }
      return true;
    });

    this.dataTableSchema.dataSource = filtered.map((row) => this.mapTableRow(row, reportOwnerValue, reportNameValue));
  }


  override onControlValueChanged = (event: FieldSelectedValue) => {
    const field = this.filterSchema.FieldsArray.find((p) => p.fieldName === event.FieldName);
    if (field) {
      field.selectedValue = event.Value;
    }
    this.changedFilterFields.add(event.FieldName);
    if (event.FieldName === FieldNames.ReportOwner) {
      const match = this.reportOwnerNameMap.find((m) => m.reportOwner === event.Value);
      const reportNameField = this.filterSchema.FieldsArray.find((p) => p.fieldName === FieldNames.ReportName);
      if (reportNameField) {
        reportNameField.selectedValue = match?.reportName ?? '';
      }
    } else if (event.FieldName === FieldNames.ReportName) {
      const match = this.reportOwnerNameMap.find((m) => m.reportName === event.Value);
      const reportOwnerField = this.filterSchema.FieldsArray.find((p) => p.fieldName === FieldNames.ReportOwner);
      if (reportOwnerField) {
        reportOwnerField.selectedValue = match?.reportOwner ?? '';
      }
    }
    this.checkForAnyEmptyFields();
  };

  override onResetClicked = (_source?: 'user' | 'navigation') => {
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) => ({ ...field, selectedValue: field['defaultValue'] ? field['defaultValue'] : '' }));
    this.updateReportFieldOptions();
    this.dataTableSchema = { ...this.dataTableSchema, inputFilter: '', dataSource: [] };
    this.rawData = [];
    this.lastFetchedReportOwner = '';
    this.lastFetchedReportName = '';
    this.lastFetchedStartDate = '';
    this.lastFetchedEndDate = '';
    this.hasFetchedReportData = false;
    this.changedFilterFields.clear();
    this.resetSorting();
    this.checkForAnyEmptyFields();
  };

  override onSearchClicked = (_searchParam: SearchConfig) => {
    const fields = this.filterSchema.FieldsArray;
    const reportName = fields.find((f) => f.fieldName === FieldNames.ReportName)?.selectedValue || '';
    const reportOwner = fields.find((f) => f.fieldName === FieldNames.ReportOwner)?.selectedValue || '';
    const startDate = this.toIsoDateString(fields.find((f) => f.fieldName === FieldNames.StartDate)?.selectedValue) || '';
    const endDate = this.toIsoDateString(fields.find((f) => f.fieldName === FieldNames.endDate)?.selectedValue) || '';
    const isReportChanged = reportName !== this.lastFetchedReportName || reportOwner !== this.lastFetchedReportOwner;
    const isDateChanged = startDate !== this.lastFetchedStartDate || endDate !== this.lastFetchedEndDate;
    if (!this.hasFetchedReportData || isReportChanged || isDateChanged) {
      this.setTableLoadingState(true);
      void this.fetchReportData(this.hasFetchedReportData);
    } else {
      this.applyClientFilters();
    }
  };

  private fetchReportData(applyFiltersAfterFetch = true): Promise<void> {
    if (!this.reportOwnersLoaded) {
      return this.fetchReportOwners().then(() => this.fetchReportData(applyFiltersAfterFetch));
    }
    this.setTableLoadingState(true);
    this.resetSorting();
    const fields = this.filterSchema.FieldsArray;
    const reportName = fields.find((f) => f.fieldName === FieldNames.ReportName)?.selectedValue || '';
    const reportOwner = fields.find((f) => f.fieldName === FieldNames.ReportOwner)?.selectedValue || '';
    const startDate = this.toIsoDateString(fields.find((f) => f.fieldName === FieldNames.StartDate)?.selectedValue) || '';
    const endDate = this.toIsoDateString(fields.find((f) => f.fieldName === FieldNames.endDate)?.selectedValue) || '';
    if (!this.isElevateReport(reportName)) {
      this.rawData = [];
      this.lastFetchedReportOwner = reportOwner;
      this.lastFetchedReportName = reportName;
      this.lastFetchedStartDate = startDate;
      this.lastFetchedEndDate = endDate;
      this.hasFetchedReportData = true;
      this.dataTableSchema = { ...this.dataTableSchema, inputFilter: '', dataSource: [] };
      this.setTableLoadingState(false);
      return Promise.resolve();
    }
    const postParam = {
      ...structuredClone(this.searchConfig),
      body: {
        [FieldNames.ReportName]: reportName,
        [FieldNames.ReportOwner]: reportOwner,
        is_ui: true,
        ...(startDate ? { [FieldNames.StartDate]: startDate } : {}),
        ...(endDate ? { [FieldNames.endDate]: endDate } : {}),
      },
    };
    this.rawData = [];
    this.dataTableSchema.dataSource = [];
    const baseUrl = (globalThis as any).environment?.[ApiEnvNames['GoogleAnalytics']];
    if (!baseUrl) {
      console.info(`Environment key '${ApiEnvNames['GoogleAnalytics']}' missing.`); 
      this.setTableLoadingState(false);
      return Promise.resolve();
    }
    const url = `${baseUrl}${postParam['path']}/{proxy+}`;
    const options = this.apiService.getHeader(postParam['headers']);
    return lastValueFrom(this.http.post(url, postParam['body'], options)).then((response: any) => {
      const rows = Array.isArray(response?.['elevate']) ? response['elevate'] : [];
      this.rawData = rows;
      this.lastFetchedReportOwner = reportOwner;
      this.lastFetchedReportName = reportName;
      this.lastFetchedStartDate = startDate;
      this.lastFetchedEndDate = endDate;
      this.hasFetchedReportData = true;
      if (applyFiltersAfterFetch) {
        this.applyClientFilters();
      } else {
        this.dataTableSchema.dataSource = rows.map((row) => this.mapTableRow(row, reportOwner, reportName));
      }
      this.setTableLoadingState(false);
    }).catch(err => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);  
      this.setTableLoadingState(false);
    });
  };
}
